/*
 *** Usage in FO: NOT OK
 *** Usage in PO: NOT OK
 *** Usage in BO: OK
 */

import Color from 'color';
import PropTypes from 'prop-types';
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Alpha, Hue, Saturation } from 'react-color/lib/components/common';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { TextInput } from '@hivebrite/design-system/Inputs';
import { POSITIONS, Tooltip } from '@hivebrite/design-system/Tooltip';

import { areSameColor, getColor, getInputColor } from './utils';

const ColorPicker = ({
  customOpacity = false,
  defaultColor,
  displayPreview = true,
  id,
  inputEnabled = true,
  onBlur,
  onChange,
  onFocus,
  presetColors = [],
  value: initialValue,
  width = 200,
}) => {
  const firstRenderRef = useRef(true);

  const [value, setValue] = useState(
    initialValue === 'transparent' || !initialValue
      ? Color('#000').alpha(0)
      : getColor(initialValue).hex(),
  );
  const [alpha, setAlpha] = useState(
    customOpacity
      ? initialValue === 'transparent' || !initialValue
        ? 0
        : getColor(initialValue).alpha()
      : 1,
  );
  const [inputValue, setInputValue] = useState(
    getInputColor(
      customOpacity && !initialValue ? 'transparent' : initialValue,
      defaultColor,
    ),
  );

  const handleInputChange = useCallback((value) => {
    setInputValue(value);

    if (value?.length === 6) {
      const newColor = getColor(`#${value}`).hex();

      setValue(newColor);
    }
  }, []);

  const handleInputBlur = () => {
    if (!inputValue?.length) {
      setValue(getInputColor(defaultColor));
    }

    onBlur?.();
  };

  const color = useMemo(
    () => getColor(value, defaultColor).alpha(alpha),
    [alpha, defaultColor, value],
  );

  useEffect(() => {
    const valueString = getInputColor(
      customOpacity && !value ? 'transparent' : value,
      defaultColor,
    );

    setInputValue(valueString);
  }, [value, color, customOpacity, defaultColor]);

  const selectedColor = value ? color.hex().toLowerCase() : null;

  // To trigger onChange
  useEffect(() => {
    if (!firstRenderRef.current) {
      const isTransparent = value === 'transparent';

      // If color is transparent, we close the picker & reset to the initial value
      if (isTransparent) {
        onChange(undefined);

        return;
      }

      const color = getColor(value);
      const newValue = customOpacity
        ? color.alpha(alpha).rgb().string().toLowerCase()
        : color.hex().toLowerCase();

      onChange(newValue);
    } else if (initialValue) {
      onChange('');
    }

    firstRenderRef.current = false;
  }, [alpha, customOpacity, initialValue, onChange, selectedColor, value]);

  const rgb = color.rgb().object();
  const hsl = color.hsl().object();
  const hsv = color.hsv().object();

  return (
    <PickerWrapper width={width}>
      <ColorToolsWrapper width={width}>
        <ColorToolWrapper height={150}>
          <Saturation
            hsl={hsl}
            hsv={{
              h: hsv.h,
              s: hsv.s / 100,
              v: hsv.v / 100,
            }}
            onChange={(value) => {
              const newHsv = {
                h: value.h,
                s: value.s * 100,
                v: value.v * 100,
              };

              if (alpha) {
                setValue(Color(newHsv).alpha(alpha));
              } else {
                setAlpha(1);
                setValue(Color(newHsv).alpha(1));
              }
            }}
          />
        </ColorToolWrapper>
        <ColorToolWrapper height={10}>
          <Hue
            hsl={hsl}
            onChange={(value) => {
              const newHsl = {
                h: value.h,
                s: value.s,
                l: value.l,
              };

              setValue(Color(newHsl).alpha(alpha));
            }}
          />
        </ColorToolWrapper>
        {customOpacity && (
          <ColorToolWrapper height={10}>
            <Alpha
              rgb={{
                ...rgb,
                a: alpha,
              }}
              hsl={hsl}
              onChange={(value) => {
                setAlpha(value.a);
              }}
            />
          </ColorToolWrapper>
        )}
        {displayPreview && (
          <Preview>
            <ColorPreview bg={color.rgb().string()} />
            <InputWrapper>
              <Box mr={1}>#</Box>
              <TextInput
                dataTestId="color-input"
                disabled={!inputEnabled}
                id={id}
                name={id}
                onBlur={handleInputBlur}
                onChange={(e) => {
                  handleInputChange(e.target.value);
                }}
                onFocus={(e) => {
                  onFocus?.(e);
                }}
                value={inputValue}
                variant="small"
              />
            </InputWrapper>
          </Preview>
        )}
      </ColorToolsWrapper>
      {Boolean(presetColors.length) && (
        <>
          <StyledHr />
          <CategoriesWrapper>
            {presetColors.map(({ name, colors }, index) => {
              const isLast = presetColors.length - 1 === index;

              return (
                <Fragment key={name}>
                  <ColorsWrapper>
                    {colors.map(({ label, value: colorValue, withTooltip }) => (
                      <Tooltip
                        key={`${label || colorValue}-tooltip`}
                        initialPosition={POSITIONS.BOTTOM}
                        childrenElement={withTooltip && label}
                      >
                        <ColorBox
                          color={colorValue}
                          onClick={() => {
                            setValue(colorValue);
                          }}
                          isActive={
                            (!selectedColor && colorValue === 'transparent') ||
                            colorValue === selectedColor ||
                            areSameColor(
                              getInputColor(inputValue, defaultColor),
                              getInputColor(colorValue, defaultColor),
                            )
                          }
                        />
                      </Tooltip>
                    ))}
                  </ColorsWrapper>
                  {!isLast && <VerticalSeparator />}
                </Fragment>
              );
            })}
          </CategoriesWrapper>
        </>
      )}
    </PickerWrapper>
  );
};

ColorPicker.propTypes = {
  customOpacity: PropTypes.bool,
  displayPreview: PropTypes.bool,
  id: PropTypes.string.isRequired,
  inputEnabled: PropTypes.bool,
  onBlur: PropTypes.func,
  onChange: PropTypes.func.isRequired,
  onFocus: PropTypes.func,
  presetColors: PropTypes.array.isRequired,
  value: PropTypes.string,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default ColorPicker;

const CategoriesWrapper = styled(Box).attrs(() => ({
  alignItems: 'center',
  justifyContent: 'space-around',
  mb: 1,
}))``;

const ColorBox = styled(Box)
  .withConfig({
    shouldForwardProp: (prop) =>
      !['color', 'defaultColor', 'isActive'].includes(prop),
  })
  .attrs(({ color }) => ({
    bg: color,
    height: 20,
    width: 20,
  }))`
  ${({ isActive, color, theme }) => `
    border: 1px solid ${isActive ? theme.colors.primary : theme.colors.dove};
    border-radius: 4px;
    cursor: pointer;

    ${
      color === 'transparent' &&
      `
        position: relative;
        overflow: hidden;

        ::before {
          position: absolute;
          content: '';
          background: red;
          display: block;
          width: 150%;
          height: 3px;
          transform: rotate(-45deg);
          left: -5px;
          right: -5px;
          top: -5px;
          bottom: -5px;
          margin: auto;
        }
      `
    }
  `}
`;

const ColorPreview = styled(Box)
  .withConfig({
    shouldForwardProp: (prop) => !['bg'].includes(prop),
  })
  .attrs(({ bg }) => ({
    bg,
    mr: 3,
    height: 24,
    width: 54,
  }))``;

const ColorsWrapper = styled(Box)`
  padding: 10px;

  > :not(:last-child) {
    margin-right: 10px;
  }
`;

const ColorToolWrapper = styled(Box).attrs(({ height }) => ({
  height,
  mb: 1,
}))`
  cursor: pointer;
  position: relative;
  user-select: none;
`;

const ColorToolsWrapper = styled(Box).attrs(() => ({
  flexDirection: 'column',
  p: 2,
}))``;

const InputWrapper = styled(Box).attrs(() => ({
  alignItems: 'center',
  flex: 1,
  minWidth: 90,
}))``;

const PickerWrapper = styled(Box).attrs(() => ({
  flexDirection: 'column',
  bg: 'white',
}))`
  box-shadow: 0 4px 30px 0 rgba(0, 0, 0, 0.2);
  border-radius: 4px;
`;

const Preview = styled(Box).attrs(() => ({
  alignItems: 'center',
  mt: 2,
}))``;

const StyledHr = styled.hr`
  margin: 0;
`;

const VerticalSeparator = styled.div`
  background-color: ${({ theme }) => theme.colors.dove};
  height: 20px;
  min-width: 2px;
`;
